import qs from "querystring";
import { jwtDecode } from "jwt-decode";
import SessionManager from "./SessionManager";

// Cognito Configuration - loaded from runtime config.js
export const COGNITO_ENDPOINT = window.APP_CONFIG.COGNITO_DOMAIN;
export const CLIENT_ID = window.APP_CONFIG.COGNITO_CLIENT_ID;
export const IDENTITY_PROVIDER = "AmazonFederate";
export const REDIRECT_URI = window.APP_CONFIG.REDIRECT_URI || `${window.location.origin}/auth`;

export const COGNITO_OAUTH_ENDPOINT = `${COGNITO_ENDPOINT}/oauth2`;

// Generate auth URL with state parameter for CSRF protection.
// The state is the only CSRF defense for the OAuth authorization-code flow,
// so it must be unguessable — generate it from a CSPRNG (window.crypto),
// not Math.random() (V8's xorshift128+ is predictable and ~25-31 bits here).
export const getCognitoAuthUrl = () => {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  const state = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  sessionStorage.setItem('oauth_state', state);
  return `${COGNITO_OAUTH_ENDPOINT}/authorize?response_type=code&client_id=${CLIENT_ID}&redirect_uri=${REDIRECT_URI}&scope=openid&identity_provider=${IDENTITY_PROVIDER}&state=${state}`;
};

// Legacy constant for backward compatibility (without state - should migrate to getCognitoAuthUrl)
export const COGNITO_AUTH_URL = `${COGNITO_OAUTH_ENDPOINT}/authorize?response_type=code&client_id=${CLIENT_ID}&redirect_uri=${REDIRECT_URI}&scope=openid&identity_provider=${IDENTITY_PROVIDER}`;

export const checkAuthTokenExists = () => {
  const accessTokenExists = !!SessionManager.getAccessToken();
  const idTokenExists = !!SessionManager.getIdToken();
  const refreshTokenExists = !!SessionManager.getRefreshToken();
  return accessTokenExists && idTokenExists && refreshTokenExists;
};

/**
 * Get the subject id for a particular user from the Id Token. "sub" is unique to an individual user.
 * return "sub" claim for a particular user.
 */
export const getSubjectId = () => {
  if (checkAuthTokenExists()) {
    const decodedIdToken = jwtDecode(SessionManager.getIdToken());

    const { sub } = decodedIdToken;
    return sub;
  }

  return "";
};

export const getUsername = () => {
  let username = "";
  try {
    if (SessionManager.getIdToken()) {
      const decodedIdToken = jwtDecode(SessionManager.getIdToken());
      const { identities } = decodedIdToken;
      if (identities && identities[0]) {
        username = identities[0].userId;
      } else {
        // Fallback to email or sub
        username = decodedIdToken.email || decodedIdToken.sub;
      }
    }
  } catch (e) {
    console.log(e);
  }
  return username;
};

export const getUserEmail = () => {
  if (checkAuthTokenExists()) {
    const decodedIdToken = jwtDecode(SessionManager.getIdToken());
    return decodedIdToken.email || "";
  }
  return "";
};

export const getUserGroups = () => {
  if (checkAuthTokenExists()) {
    const decodedIdToken = jwtDecode(SessionManager.getIdToken());
    const profile = decodedIdToken.profile || '';
    try {
      return profile ? JSON.parse(profile) : [];
    } catch {
      return [];
    }
  }
  return [];
};

export const login = (
  id_token,
  access_token,
  refresh_token,
  tokenExpiration
) => {
  SessionManager.setIdToken(id_token);
  SessionManager.setAccessToken(access_token);
  SessionManager.setRefreshToken(refresh_token);
  SessionManager.setTokenExpiration(tokenExpiration);
};

export const logout = () => {
  SessionManager.removeIdToken();
  SessionManager.removeAccessToken();
  SessionManager.removeRefreshToken();
  SessionManager.removeTokenExpiration();
};

export const logoutAndRedirect = () => {
  logout();
  // Match APFP behavior: just reload the page to force re-authentication
  window.location.replace(window.location.href);
};

export const tokenExpired = () => {
  const tokenExpiration = SessionManager.getTokenExpiration();
  const expirationTime = new Date(tokenExpiration);
  const today = new Date();
  return today > expirationTime;
};

export const isAuthTokenValid = async () => {
  let valid = false;

  if (checkAuthTokenExists() && !tokenExpired()) {
    valid = true;
  } else {
    try {
      valid = await refreshToken();
    } catch (e) {
      console.error(e);
    }
  }
  return valid;
};

export const checkTokenExpiration = async () => {
  if (checkAuthTokenExists()) {
    if (tokenExpired()) {
      console.log("Token expired, attempting refresh...");
      try {
        const refreshed = await refreshToken();
        if (!refreshed) {
          console.log("Token refresh failed, redirecting to login");
          SessionManager.setOriginUrl(window.location.pathname);
          window.location.replace(getCognitoAuthUrl());
        }
      } catch (err) {
        console.error("Token refresh error:", err);
        logout();
        SessionManager.setOriginUrl(window.location.pathname);
        window.location.replace(getCognitoAuthUrl());
      }
    } else {
      return true;
    }
  } else {
    console.log("No authentication found, redirecting user...");
    logout();
    SessionManager.setOriginUrl(window.location.pathname);
    window.location.replace(getCognitoAuthUrl());
  }
};

export const refreshToken = async () => {
  const refresh_token = SessionManager.getRefreshToken();

  if (!refresh_token) {
    console.error("No refresh token available");
    logout();
    return false;
  }

  const authReqBody = {
    grant_type: "refresh_token",
    client_id: CLIENT_ID,
    refresh_token: refresh_token,
  };

  const headers = {
    "Content-Type": "application/x-www-form-urlencoded",
  };

  try {
    const response = await fetch(`${COGNITO_OAUTH_ENDPOINT}/token`, {
      method: "POST",
      headers,
      body: qs.stringify(authReqBody),
    });

    if (response.ok) {
      const authPayload = await response.json();
      const expires_in = authPayload.expires_in
        ? parseInt(authPayload.expires_in) / 60
        : 60;
      let expireDate = new Date();
      expireDate.setTime(
        expireDate.setMinutes(expireDate.getMinutes() + expires_in)
      );

      const id_token = authPayload.id_token;
      const access_token = authPayload.access_token;

      // Set the tokens and expiration in session storage.
      login(id_token, access_token, refresh_token, expireDate.toUTCString());
      return true;
    } else {
      const errorText = await response.text();
      console.error("Token refresh failed:", response.status, errorText);
      logout();
      return false;
    }
  } catch (error) {
    console.error("Token refresh error:", error);
    logout();
    return false;
  }
};
